/**
 * Mega menu behaviour.
 *
 * <mega-menu>         Desktop (≥ 990px) panels under the navbar
 *                     (markup: blocks/_header-menu.liquid + snippets/mega-menu-panel.liquid).
 * <mega-menu-drawer>  Mobile drawer that slides level by level
 *                     (markup: snippets/mega-menu-drawer.liquid).
 */

const DESKTOP = window.matchMedia('(min-width: 990px)');
const OPEN_DELAY = 150;
const CLOSE_DELAY = 250;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]';

/**
 * @param {Element} el
 * @returns {boolean}
 */
function isTabbable(el) {
  return (
    el instanceof HTMLElement &&
    el.tabIndex >= 0 &&
    !el.closest('[hidden]') &&
    el.getClientRects().length > 0 &&
    getComputedStyle(el).visibility !== 'hidden'
  );
}

/**
 * @param {ParentNode} root
 * @returns {HTMLElement[]}
 */
function tabbables(root) {
  return /** @type {HTMLElement[]} */ ([...root.querySelectorAll(FOCUSABLE)].filter(isTabbable));
}

/** @type {Map<string, Promise<string>>} section HTML per URL, shared by all panels */
const productsCache = new Map();

/**
 * Fill a "Suosituimmat" row (sections/mega-menu-products.liquid) the first
 * time its pane is shown. Placeholders stay until the HTML arrives. The row is
 * hidden for good if the collection has no products; a failed request hides it
 * only until the pane is shown again, which retries.
 * @param {Element | null} pane
 */
function loadProducts(pane) {
  const row = pane?.querySelector('[data-mm-products]');
  if (!(row instanceof HTMLElement)) return;
  const state = row.dataset.loaded;
  if (state && state !== 'failed') return;
  const url = row.dataset.url;
  if (!url) return;

  row.hidden = false;
  row.dataset.loaded = 'loading';
  let request = productsCache.get(url);
  if (!request) {
    request = fetch(url).then((response) => (response.ok ? response.text() : Promise.reject(response.status)));
    productsCache.set(url, request);
    request.catch(() => productsCache.delete(url));
  }

  request
    .then((html) => {
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mm-products-list]');
      const current = row.querySelector('[data-mm-products-list]');
      if (!list || !list.children.length || !current) {
        row.hidden = true;
        row.dataset.loaded = 'empty';
        return;
      }
      current.replaceWith(document.importNode(list, true));
      row.dataset.loaded = 'true';
    })
    .catch(() => {
      row.hidden = true;
      row.dataset.loaded = 'failed';
    });
}

class MegaMenu extends HTMLElement {
  /** @type {HTMLElement | null} */
  current = null;
  /** @type {'hover' | 'click' | 'keyboard' | null} */
  openedBy = null;
  /** @type {HTMLElement | null} */
  pending = null;
  openTimer = 0;
  closeTimer = 0;

  connectedCallback() {
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeyDown);
    DESKTOP.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    this.#cancelTimers();
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeyDown);
    DESKTOP.removeEventListener('change', this.#onBreakpointChange);
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {HTMLElement | null}
   */
  panelFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  get isOpen() {
    return Boolean(this.current && this.current.getAttribute('aria-expanded') === 'true');
  }

  /**
   * @param {HTMLElement} trigger
   * @param {'hover' | 'click' | 'keyboard'} by
   */
  open(trigger, by) {
    this.#cancelTimers();
    if (trigger === this.current && this.isOpen) return;

    this.close();
    const panel = this.panelFor(trigger);
    if (!panel) return;

    // Keep the panel inside the viewport on short screens.
    const anchor = this.closest('.header__row') || this;
    const top = anchor.getBoundingClientRect().bottom;
    panel.style.setProperty('--mm-max-h', `${Math.max(320, Math.round(window.innerHeight - top - 16))}px`);

    const firstTab = panel.querySelector('[data-mm-tab]');
    if (firstTab instanceof HTMLElement) {
      this.#activateTab(firstTab);
    } else {
      // Compact panel: a single pane, always shown
      loadProducts(panel.querySelector('[data-mm-pane]'));
    }

    panel.hidden = false;
    const overlay = this.querySelector('[data-mm-overlay]');
    if (overlay instanceof HTMLElement) overlay.hidden = false;

    trigger.setAttribute('aria-expanded', 'true');
    this.current = trigger;
    this.openedBy = by;
  }

  close() {
    this.#cancelTimers();
    this.querySelectorAll('[data-mm-pane]').forEach((pane) => this.#closeSub(pane, false));
    this.querySelectorAll('[data-mm-trigger][aria-expanded="true"]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('[data-mm-panel], [data-mm-overlay]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = true;
    });
    this.current = null;
    this.openedBy = null;
  }

  /* ── Timers ── */

  /** @param {HTMLElement} trigger */
  #scheduleOpen(trigger) {
    if (this.pending === trigger) return;
    clearTimeout(this.openTimer);
    this.pending = trigger;
    this.openTimer = window.setTimeout(() => {
      this.pending = null;
      this.open(trigger, 'hover');
    }, OPEN_DELAY);
  }

  #scheduleClose() {
    if (!this.current || this.closeTimer) return;
    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = 0;
      this.close();
    }, CLOSE_DELAY);
  }

  #cancelOpen() {
    clearTimeout(this.openTimer);
    this.pending = null;
  }

  #cancelClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = 0;
  }

  #cancelTimers() {
    this.#cancelOpen();
    this.#cancelClose();
  }

  /* ── Level 2 (left column) ── */

  /** @param {HTMLElement} tab */
  #activateTab(tab) {
    const panel = tab.closest('[data-mm-panel]');
    if (!panel) return;

    panel.querySelectorAll('[data-mm-tab]').forEach((t) => {
      if (!(t instanceof HTMLElement)) return;
      const active = t === tab;
      t.classList.toggle('is-active', active);
      t.tabIndex = active ? 0 : -1;
      const pane = document.getElementById(t.dataset.mmTab || '');
      if (pane) pane.hidden = !active;
      if (active) loadProducts(pane);
      else this.#closeSub(pane, false);
    });
  }

  /* ── Level 4 (sub-view inside a pane, from a "mega-<handle>" menu) ── */

  /** @param {HTMLElement} opener */
  #openSub(opener) {
    const pane = opener.closest('[data-mm-pane]');
    const view = document.getElementById(opener.getAttribute('aria-controls') || '');
    if (!pane || !view) return;

    // Show and focus the new view before hiding the old one, so focus never
    // lands on a hidden element (which would blur it out of the panel).
    view.classList.add('is-active');
    opener.setAttribute('aria-expanded', 'true');
    const main = pane.querySelector('.mm-pane__main');
    if (main) main.scrollTop = 0;
    /** @type {HTMLElement | null} */ (view.querySelector('[data-mm-sub-title]'))?.focus({ preventScroll: true });
    pane.querySelector('[data-mm-root-view]')?.classList.remove('is-active');
  }

  /**
   * Back to the pane's list. Returns whether a sub-view was open.
   * @param {Element | null} pane
   * @param {boolean} restoreFocus - Move focus back to the row that opened it
   */
  #closeSub(pane, restoreFocus) {
    const opener = pane?.querySelector('[data-mm-sub-open][aria-expanded="true"]');
    if (!pane || !(opener instanceof HTMLElement)) return false;

    pane.querySelector('[data-mm-root-view]')?.classList.add('is-active');
    opener.setAttribute('aria-expanded', 'false');
    if (restoreFocus) opener.focus({ preventScroll: true });
    document.getElementById(opener.getAttribute('aria-controls') || '')?.classList.remove('is-active');
    return true;
  }

  /** @param {HTMLElement} trigger */
  #focusAfter(trigger) {
    this.close();
    const all = tabbables(document).filter((el) => !el.closest('.mm-layer'));
    const next = all[all.indexOf(trigger) + 1];
    (next || trigger).focus();
  }

  /* ── Events ── */

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !DESKTOP.matches) return;
    const target = /** @type {Element} */ (event.target);

    const tab = target.closest('[data-mm-tab]');
    if (tab instanceof HTMLElement) this.#activateTab(tab);

    if (target.closest('[data-mm-panel]')) {
      this.#cancelTimers();
      return;
    }

    const item = target.closest('[data-mm-item]');
    if (item) {
      const trigger = item.querySelector('[data-mm-trigger]');
      if (!(trigger instanceof HTMLElement)) {
        // A plain link (e.g. Etusivu): close whatever is open.
        this.#cancelOpen();
        this.#scheduleClose();
        return;
      }
      this.#cancelClose();
      if (trigger === this.current && this.isOpen) {
        this.#cancelOpen();
      } else {
        this.#scheduleOpen(trigger);
      }
      return;
    }

    // Gaps in the navbar, or the dimmed page under the panel.
    this.#cancelOpen();
    this.#scheduleClose();
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    if (event.pointerType !== 'mouse' || !DESKTOP.matches) return;
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#cancelOpen();
    this.#scheduleClose();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!DESKTOP.matches) return;
    const target = /** @type {Element} */ (event.target);

    if (target.closest('[data-mm-overlay]')) {
      this.close();
      return;
    }

    const subOpener = target.closest('[data-mm-sub-open]');
    if (subOpener instanceof HTMLElement) {
      this.#openSub(subOpener);
      return;
    }

    if (target.closest('[data-mm-sub-back]')) {
      this.#closeSub(target.closest('[data-mm-pane]'), true);
      return;
    }

    const trigger = target.closest('[data-mm-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    // The navbar item opens its panel; "Kaikki …" inside it goes to the page.
    event.preventDefault();

    if (trigger === this.current && this.isOpen) {
      // A click right after hover-opening would otherwise close it again.
      if (this.openedBy === 'hover') {
        this.openedBy = 'click';
      } else {
        this.close();
      }
      return;
    }
    this.open(trigger, 'click');
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!DESKTOP.matches) return;
    const target = /** @type {Element} */ (event.target);

    const trigger = target.closest('[data-mm-trigger]');
    if (trigger instanceof HTMLElement) {
      const panel = this.panelFor(trigger);
      if (!panel) return;

      if (event.key === 'ArrowDown' || event.key === ' ') {
        event.preventDefault();
        this.open(trigger, 'keyboard');
        if (event.key === 'ArrowDown') {
          /** @type {HTMLElement | null} */ (panel.querySelector('[data-mm-tab].is-active'))?.focus();
        }
      } else if (event.key === 'Tab' && !event.shiftKey && trigger === this.current && this.isOpen) {
        // The panel sits after the whole navbar in the DOM; jump into it.
        event.preventDefault();
        tabbables(panel)[0]?.focus();
      }
      return;
    }

    const panel = target.closest('[data-mm-panel]');
    if (!(panel instanceof HTMLElement) || !this.current) return;

    // Esc inside a sub-view goes back to the list instead of closing the panel.
    if (event.key === 'Escape' && this.#closeSub(target.closest('[data-mm-pane]'), true)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const tab = target.closest('[data-mm-tab]');
    const tabs = /** @type {HTMLElement[]} */ ([...panel.querySelectorAll('[data-mm-tab]')]);

    if (tab instanceof HTMLElement && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const index = tabs.indexOf(tab);
      let next = index;
      if (event.key === 'ArrowDown') next = Math.min(index + 1, tabs.length - 1);
      if (event.key === 'ArrowUp') next = Math.max(index - 1, 0);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      tabs[next]?.focus();
      return;
    }

    if (tab instanceof HTMLElement && event.key === 'ArrowRight') {
      const pane = document.getElementById(tab.dataset.mmTab || '');
      const first = pane && tabbables(pane)[0];
      if (first) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (target.matches('.mm-panel__all') && event.key === 'ArrowDown') {
      event.preventDefault();
      tabs.find((t) => t.classList.contains('is-active'))?.focus();
      return;
    }

    if (event.key === 'Tab') {
      const items = tabbables(panel);
      if (!event.shiftKey && target === items[items.length - 1]) {
        event.preventDefault();
        this.#focusAfter(this.current);
      } else if (event.shiftKey && target === items[0]) {
        event.preventDefault();
        this.current.focus();
      }
    }
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const tab = /** @type {Element} */ (event.target).closest('[data-mm-tab]');
    if (tab instanceof HTMLElement) this.#activateTab(tab);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    // Only when focus actually moves elsewhere: clicking a blank spot inside
    // the panel blurs to <body> (relatedTarget null) and must not close it.
    if (event.relatedTarget instanceof Node && !this.contains(event.relatedTarget)) this.close();
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.current) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.current) return;
    const trigger = this.current;
    const hadFocus = this.contains(document.activeElement);
    this.close();
    if (hadFocus) trigger.focus();
  };

  #onBreakpointChange = () => {
    this.close();
  };
}

class MegaMenuDrawer extends HTMLElement {
  /** @type {HTMLElement[]} buttons that opened each view, deepest last */
  stack = [];
  /** @type {HTMLDetailsElement | null} */
  details = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keyup', this.#onKeyUp);
    this.details = this.closest('details');
    this.details?.addEventListener('toggle', this.#onToggle);
  }

  disconnectedCallback() {
    this.details?.removeEventListener('toggle', this.#onToggle);
  }

  get activeView() {
    return this.querySelector('[data-mm-view].is-active');
  }

  /** @param {HTMLElement} opener */
  forward(opener) {
    const next = document.getElementById(opener.getAttribute('aria-controls') || '');
    const current = this.activeView;
    if (!next || !current) return;

    this.stack.push(opener);
    opener.setAttribute('aria-expanded', 'true');
    current.classList.remove('is-active');
    current.classList.add('is-behind');
    next.classList.remove('is-behind');
    next.classList.add('is-active');
    this.#scrollToTop();
    const focusTarget = next.querySelector('[data-mm-title]') || next.querySelector('[data-mm-back]');
    /** @type {HTMLElement | null} */ (focusTarget)?.focus({ preventScroll: true });
  }

  back() {
    const opener = this.stack.pop();
    const current = this.activeView;
    const previous = opener?.closest('[data-mm-view]');
    if (!opener || !current || !previous) return;

    current.classList.remove('is-active');
    previous.classList.remove('is-behind');
    previous.classList.add('is-active');
    opener.setAttribute('aria-expanded', 'false');
    this.#scrollToTop();
    opener.focus({ preventScroll: true });
  }

  /** Back to the top level without animating (used when the drawer closes). */
  reset() {
    this.classList.add('is-resetting');
    this.querySelectorAll('[data-mm-view]').forEach((view, index) => {
      view.classList.remove('is-behind');
      view.classList.toggle('is-active', index === 0);
    });
    this.querySelectorAll('[data-mm-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    this.stack = [];
    void this.offsetHeight;
    this.classList.remove('is-resetting');
  }

  #scrollToTop() {
    const scroller = this.closest('.menu-drawer');
    if (scroller) scroller.scrollTop = 0;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const opener = target.closest('[data-mm-open]');
    if (opener instanceof HTMLElement) {
      event.preventDefault();
      this.forward(opener);
      return;
    }

    if (target.closest('[data-mm-back]')) {
      event.preventDefault();
      this.back();
    }
  };

  /**
   * Esc on a sub-level goes back one level. header-drawer.js closes the whole
   * drawer on Escape keyup further up, so stop it here while there's a level to go back to.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.stack.length) return;
    event.stopPropagation();
    this.back();
  };

  #onToggle = () => {
    if (this.details && !this.details.open) this.reset();
  };
}

if (!customElements.get('mega-menu')) customElements.define('mega-menu', MegaMenu);
if (!customElements.get('mega-menu-drawer')) customElements.define('mega-menu-drawer', MegaMenuDrawer);
